<template>
  <a
    class="e-file-loaded-card paragraph-xs text-secondary"
    :href="file.url"
    target="_blank"
    :class="{ square: mode === 'square', loading: file.loading }"
  >
    <div class="w-100">
      <div class="e-file-loaded-card__content">
        <e-icon v-if="file.type === FileType.PDF" name="pdf" :size="20" class="text-blue" />
        <div class="e-file-loaded-card__name">{{ fileName }}</div>
        <b v-if="mode === 'row'" class="e-file-loaded-card__size">{{ formatBytes(file.size, 0) }}</b>
      </div>
      <div v-if="file.error" class="input-placeholder text-red mt-1" data-form-error>
        {{ file.error }}
      </div>
    </div>

    <v-spacer />
    <e-btn
      v-if="deleteable"
      icon
      size="S"
      :variant="mode === 'square' ? 'tonal' : 'ghost'"
      class="e-file-loaded-card__delete"
      :loading="file.loading"
      @click.stop.prevent="onDeleteClick"
    >
      <e-icon name="trash" :size="20" />
    </e-btn>
  </a>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import  { FileType, type LoadedFile } from '@/core/types/files';
import { EBtn, EIcon } from '@/core/components/ui';
import { formatBytes } from '@/core/utils/file';

const props = withDefaults(
  defineProps<{
    file: LoadedFile;
    deleteable?: boolean;
    mode?: 'row' | 'square';
  }>(),
  {
    deleteable: true,
    mode: 'row',
  },
);

const emit = defineEmits<{
  (event: 'delete'): void;
}>();

const fileName = computed(() => {
  const nameArray = props.file.name.split('.');
  nameArray.pop();

  const joinedName = nameArray.join('.');

  const firstPart = joinedName.slice(0, 60);

  const suffixLength = Math.min(joinedName.length - firstPart.length, 2);
  const suffix = suffixLength > 0 ? `... ${joinedName.slice(joinedName.length - suffixLength)}` : '';

  return `${firstPart}${suffix}${props.file.extension}`;
});

const onDeleteClick = () => {
  emit('delete');
};
</script>

<style scoped lang="scss">
$root: 'e-file-loaded-card';

.#{$root} {
  position: relative;

  display: flex;
  align-items: center;

  min-height: 44px;
  padding: 8px 6px 8px 12px;

  background-color: $bg-light-color;
  border-radius: $input-border-radius;

  @include hover {
    .#{$root}__name {
      color: $text-link-color !important;
    }
  }

  &.square {
    aspect-ratio: 1/1;

    padding: 8px;

    background-color: $primary-light-color;
    border: 1px solid $bg-light-color;

    &.loading {
      .#{$root}__delete {
        opacity: 1;
      }
    }

    .#{$root} {
      &__content {
        flex-direction: column;
      }

      &__name {
        margin-top: 4px;

        text-align: center;
        color: $text-secondary-color;

        @include paragraph-xxs;
      }

      &__delete {
        position: absolute;
        top: 8px;
        right: 8px;

        opacity: 0;
        transition: opacity $transition-duration;
      }
    }

    @include hover {
      .#{$root}__delete {
        opacity: 1;
      }
    }
  }

  &__content {
    display: flex;
    align-items: center;
  }

  &__size {
    margin: 0 8px;

    white-space: nowrap;
  }

  &__name {
    word-break: break-word;
  }
}
</style>
